import { useMutation } from '@apollo/client';
import type { WorkflowCompletion } from '@interfaces/workflow';
import { ActionContext } from '@ovComponents/5-page/viewPage/actionContext';
import { useClientContext } from '@pages/client/clientContext';
import { useHouseholdContext } from '@pages/household/householdContext';
import { UserContext, usePermissions } from '@providers/userContextProvider';
import {
  type ComponentProps,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  CREATE_WORKFLOW_COMPLETION,
  UPDATE_WORKFLOW_COMPLETION,
} from '../subSteps/queries';
import { WorkflowContext } from '../workflowCompletionContext';
import type { WorkflowDataType } from '../workflowTypes';
import { InnerWorkflowModal } from './innerWorkflowModal';
import type { WorkflowCompletionWithId } from './workflowCompletionWithId.types';

export const useInnerWorkflow = ({
  workflowCompletion,
  refetch,
}: {
  workflowCompletion: WorkflowCompletionWithId;
  refetch: any;
}) => {
  const { workflowData } = useContext(WorkflowContext);
  const { refetch: refetchWorkflow } = useContext(ActionContext);
  const { permissions } = usePermissions();
  const [innerOpen, setInnerOpen] = useState(false);
  const [newActiveWorkflow, setNewActiveWorkflow] =
    useState<WorkflowCompletion>();
  const [workflowCompletedCallback, setWorkflowCompletedCallback] = useState<
    ((completedContext: any) => void) | null
  >(null);
  const [updateWorkflowCompletion] = useMutation(
    UPDATE_WORKFLOW_COMPLETION(permissions),
  );
  const [createWorkflowCompletionMutation] = useMutation(
    CREATE_WORKFLOW_COMPLETION(permissions),
  );

  const clientContext = useClientContext();
  const { activeOrganization } = useContext(UserContext);
  const householdContext = useHouseholdContext();
  const organizationId =
    clientContext?.orgSettings?.id ||
    householdContext?.orgSettings?.id ||
    activeOrganization?.id;

  const updateParentWorkflowCompletion = async (
    parentWorkflowComplationId: string,
    updatedContext: any,
  ) => {
    await updateWorkflowCompletion({
      variables: {
        input: {
          workflowCompletionId: parentWorkflowComplationId,
          context: updatedContext ?? undefined,
        },
      },
    });
  };
  const createWorkflowCompletion = async (options: {
    workflowId: string;
    currentUserId?: string;
    workflowContext?: WorkflowDataType;
    onWorkflowCompleted?: (completedContext: any) => void;
  }) => {
    const result = await createWorkflowCompletionMutation({
      variables: {
        input: {
          objectId: options.currentUserId,
          objectType: 'USER',
          workflowId: options.workflowId,
          organizationId,
          parentWorkflowCompletionId: workflowCompletion.id,
        },
      },
    });
    const { data } = result;
    setNewActiveWorkflow({
      ...data.createWorkflowCompletion.workflowCompletion,
      context: options?.workflowContext || workflowData,
    });
    // Store the completion callback for this workflow
    if (options.onWorkflowCompleted)
      setWorkflowCompletedCallback(() => options.onWorkflowCompleted);
    if (workflowCompletion?.id) {
      const updatingContext = {
        ...workflowData,
        childWorkflowCompletion:
          data.createWorkflowCompletion.workflowCompletion,
      };
      await updateParentWorkflowCompletion(
        workflowCompletion.id,
        updatingContext,
      );
    }
    setInnerOpen(true);
    return result;
  };

  const lastContextRef = useRef();
  const onUpdateParent: ComponentProps<
    typeof InnerWorkflowModal
  >['updateParent'] = async (activeCompletion?: any) => {
    if (!activeCompletion) {
      delete workflowData.childWorkflowCompletion;
      await updateParentWorkflowCompletion(workflowCompletion.id, {
        ...workflowData,
      });

      // Call the completion callback if provided and workflow is completed
      await workflowCompletedCallback?.(lastContextRef.current);
    } else {
      const updatingContext = {
        ...workflowData,
        childWorkflowCompletion: activeCompletion,
      };
      await updateParentWorkflowCompletion(
        workflowCompletion.id,
        updatingContext,
      );
      lastContextRef.current = activeCompletion.context;
    }
  };

  const onRefetch: ComponentProps<typeof InnerWorkflowModal>['refetch'] =
    async () => {
      await Promise.all([refetchWorkflow(), refetch()]);
    };

  useEffect(() => {
    if (workflowCompletion?.context?.childWorkflowCompletion) {
      setNewActiveWorkflow({
        ...workflowCompletion?.context?.childWorkflowCompletion,
        context: workflowData,
      });
    }
  }, [workflowCompletion, workflowData]);

  return {
    createWorkflowCompletion,
    innerWorkflowModal: newActiveWorkflow && (
      <InnerWorkflowModal
        newActiveWorkflow={newActiveWorkflow}
        setOpen={setInnerOpen}
        open={innerOpen}
        refetch={onRefetch}
        updateParent={onUpdateParent}
      />
    ),
  };
};
